// @ts-nocheck
import React, { useEffect, useRef, useState } from "react"
import { useLocation, useNavigationType } from "react-router-dom"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { UnsavedChangesProvider } from "../common/UnsavedChangesGuard.jsx"
import { MobileTopBar, NavBackdrop, NavMainColumn } from "../nav/index.js"
import { NavShellProvider } from "../nav/navShellContext.js"
import { LocationSelector } from "./LocationSelector.jsx"
import "./v2-shell.css"

const logoBadge = "SJ"
const defaultLogoSrc = new URL("../assets/STJ Round Logo Mobile.png", import.meta.url).href
const defaultLogoAlt = "St. Jean's Sport Processing Logo"
const sectionDividerBorderColor = "rgba(255, 255, 255, 0.06)"

/**
 * @param {{
 *   title: string,
 *   subtitle?: string,
 *   useLocationSelector?: boolean,
 *   locationId?: number | null,
 *   locations?: any[],
 *   onLocationChange?: (locationId: number) => void,
 *   navItems: React.ReactNode[],
 *   footerItems?: React.ReactNode[],
 *   mobileTopBarActions?: React.ReactNode,
 *   children: React.ReactNode,
 *   onNotImplemented?: () => void,
 * }} props
 */
export function Layout({
    title,
    subtitle = "",
    useLocationSelector = false,
    locationId = null,
    locations = [],
    onLocationChange = () => {},
    navItems,
    footerItems = [],
    mobileTopBarActions = null,
    children,
    onNotImplemented = () => {},
}) {
    // Prefer site header env override when provided.
    // @ts-ignore - Vite injects `import.meta.env` at build time
    const siteHeader = (import.meta.env?.VITE_SITE_HEADER ?? "").trim()
    const effectiveTitle = siteHeader || title

    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false)
    const [isSidebarOpen, setIsSidebarOpen] = useState(false)
    const location = useLocation()
    const navType = useNavigationType()
    const prevPathnameRef = useRef(location.pathname)

    useEffect(() => {
        const html = document.documentElement
        const body = document.body
        const originalFontSize = html.style.fontSize
        html.style.fontSize = "16px"
        html.classList.add("v2-route")
        body.classList.add("v2-route")

        return () => {
            html.style.fontSize = originalFontSize
            html.classList.remove("v2-route")
            body.classList.remove("v2-route")
        }
    }, [])

    useEffect(() => {
        if (navType === "POP") return
        if (location.state?.preventScrollReset) return
        if (location.pathname === prevPathnameRef.current) return
        /** @type {HTMLElement | null} */
        const activeEl =
            document.activeElement instanceof HTMLElement ? document.activeElement : null
        activeEl?.blur?.()
        window.scrollTo({ top: 0, left: 0, behavior: "instant" })
        prevPathnameRef.current = location.pathname
    }, [location.pathname, location.state, navType])

    const isDesktop = typeof window === "undefined" ? true : window.innerWidth >= 992
    const isCollapsed = isSidebarCollapsed && isDesktop

    function closeSidebar() {
        setIsSidebarOpen(false)
    }

    return (
        <UnsavedChangesProvider>
        <div className="v2-page sj-nav-shell min-vh-100 position-relative">
            <NavBackdrop visible={isSidebarOpen} onClose={closeSidebar} />

            <NavShellProvider value={{ closeSidebar, isCollapsed, onNotImplemented }}>
                <div
                    className={
                        "sidebar position-fixed top-0 start-0 h-100 shadow-lg d-flex flex-column" +
                        (isSidebarOpen ? " show" : "") +
                        (isCollapsed ? " collapsed" : "")
                    }
                    style={{
                        backgroundColor: "#0f6170",
                        width: isCollapsed ? "80px" : "288px",
                        zIndex: 1050,
                    }}
                >
                    <div
                        className="sidebar-header p-4 border-bottom position-relative flex-shrink-0"
                        style={{ borderColor: sectionDividerBorderColor }}
                    >
                        <div
                            className={
                                "d-flex flex-column align-items-center " +
                                (isCollapsed ? "gap-2" : "gap-3")
                            }
                        >
                            {defaultLogoSrc ? (
                                <img
                                    src={defaultLogoSrc}
                                    alt={defaultLogoAlt}
                                    className="h-auto"
                                    style={{
                                        width: isCollapsed ? "32px" : "64px",
                                        transition: "width 0.3s ease-in-out",
                                    }}
                                />
                            ) : (
                                <div className="v2-logo-badge">{logoBadge}</div>
                            )}
                            <h1 className="fs-4 fw-semibold text-white mb-0 nav-text text-center">
                                {effectiveTitle}
                            </h1>
                            {subtitle && !isCollapsed ? (
                                <div className="text-white-50 nav-text text-center" style={{ fontSize: "0.875rem" }}>
                                    {subtitle}
                                </div>
                            ) : null}
                            {useLocationSelector ? (
                                <LocationSelector
                                    value={locationId}
                                    locations={locations}
                                    collapsed={isCollapsed}
                                    onChange={onLocationChange}
                                />
                            ) : null}
                        </div>

                        <button
                            type="button"
                            onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                            className={
                                "btn btn-link text-white p-0 border-0 d-none d-lg-flex " +
                                "position-absolute align-items-center justify-content-center"
                            }
                            style={{
                                right: "-12px",
                                top: "50%",
                                transform: "translateY(-50%)",
                                backgroundColor: "#0f6170",
                                borderRadius: "50%",
                                width: "24px",
                                height: "24px",
                                boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                            }}
                            aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                            title={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
                        >
                            {isCollapsed ? (
                                <ChevronRight size={16} aria-hidden="true" />
                            ) : (
                                <ChevronLeft size={16} aria-hidden="true" />
                            )}
                        </button>
                    </div>

                    <nav className="sidebar-nav p-3 flex-grow-1 overflow-auto" aria-label="Primary navigation">
                        <div className="d-flex flex-column gap-2">{navItems}</div>
                    </nav>

                    {footerItems.length ? (
                        <div
                            className="sidebar-footer p-3 border-top mt-auto flex-shrink-0"
                            style={{ borderColor: sectionDividerBorderColor }}
                        >
                            <div className="d-flex flex-column gap-2">{footerItems}</div>
                        </div>
                    ) : null}
                </div>

                <NavMainColumn
                    isSidebarCollapsed={isSidebarCollapsed}
                    mobileTopBar={
                        <MobileTopBar
                            logoSrc={defaultLogoSrc}
                            logoAlt={defaultLogoAlt}
                            title={effectiveTitle}
                            menuOpen={isSidebarOpen}
                            onToggleMenu={() => setIsSidebarOpen(!isSidebarOpen)}
                            trailingActions={mobileTopBarActions}
                        />
                    }
                >
                    {children}
                </NavMainColumn>
            </NavShellProvider>
        </div>
        </UnsavedChangesProvider>
    )
}

